import { useCallback, useEffect, useState } from "react";
import { Megaphone, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Comunicados() {
  const { user } = useAuth();
  const esManager = user?.rol === "manager";
  const [centros, setCentros] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [preview, setPreview] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [form, setForm] = useState({
    asunto: "",
    mensaje: "",
    centro_id: "",
    departamento_id: "",
    puesto_id: "",
    usuario_id: "",
    canal_email: true,
    canal_whatsapp: false,
    canal_sms: false,
  });

  useEffect(() => {
    (async () => {
      try {
        const [c, d, p, e] = await Promise.all([
          api.get("/centros"),
          api.get("/departamentos"),
          api.get("/puestos"),
          api.get("/usuarios", { params: { rol: "worker" } }).catch(() => api.get("/usuarios")),
        ]);
        setCentros(c.data || []);
        const deps = d.data || [];
        setDepartamentos(deps);
        setPuestos(p.data || []);
        const lista = Array.isArray(e.data) ? e.data : e.data?.usuarios || [];
        let emps = lista.filter((u) => ["worker", "manager", "admin"].includes(u.rol) && u.activo !== false);
        if (esManager && user?.departamento_id) {
          emps = emps.filter((u) => u.departamento_id === user.departamento_id);
        }
        setEmpleados(emps);
        if (esManager && user?.departamento_id) {
          setForm((prev) => ({ ...prev, departamento_id: String(user.departamento_id) }));
        }
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, [esManager, user?.departamento_id]);

  const paramsFiltro = useCallback(() => {
    const p = {};
    if (form.centro_id) p.centro_id = Number(form.centro_id);
    if (form.departamento_id) p.departamento_id = Number(form.departamento_id);
    if (form.puesto_id) p.puesto_id = Number(form.puesto_id);
    if (form.usuario_id) p.usuario_id = Number(form.usuario_id);
    return p;
  }, [form]);

  const cargarPreview = async () => {
    try {
      const { data } = await api.get("/comunicados/previsualizar", { params: paramsFiltro() });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    const t = setTimeout(cargarPreview, 300);
    return () => clearTimeout(t);
  }, [paramsFiltro]);

  const enviar = async () => {
    if (!form.asunto.trim() || !form.mensaje.trim()) {
      toast.error("Indica asunto y mensaje");
      return;
    }
    if (!form.canal_email && !form.canal_whatsapp && !form.canal_sms) {
      toast.error("Selecciona al menos un canal");
      return;
    }
    setEnviando(true);
    try {
      const body = {
        asunto: form.asunto.trim(),
        mensaje: form.mensaje.trim(),
        canal_email: form.canal_email,
        canal_whatsapp: form.canal_whatsapp,
        canal_sms: form.canal_sms,
      };
      if (form.centro_id) body.centro_id = Number(form.centro_id);
      if (form.departamento_id) body.departamento_id = Number(form.departamento_id);
      if (form.puesto_id) body.puesto_id = Number(form.puesto_id);
      if (form.usuario_id) body.usuario_id = Number(form.usuario_id);
      const { data } = await api.post("/comunicados", body);
      toast.success(
        `Comunicado enviado a ${data.destinatarios} persona(s)` +
          (data.enviados_externos != null ? ` · ${data.enviados_externos} por canal externo` : "")
      );
      setForm((f) => ({ ...f, asunto: "", mensaje: "" }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEnviando(false);
    }
  };

  const deptsFiltrados = (() => {
    let deps = form.centro_id
      ? departamentos.filter((d) => String(d.centro_id) === String(form.centro_id))
      : departamentos;
    if (esManager && user?.departamento_id) {
      deps = deps.filter((d) => d.id === user.departamento_id);
    }
    return deps;
  })();

  return (
    <div className="space-y-5" data-testid="comunicados-page">
      <PageHeader
        icon={Megaphone}
        title="Comunicados"
        subtitle="Envía avisos a trabajadores filtrando por centro, departamento, puesto o persona"
        sectionKey="comunicados"
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-4 rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
          <div className="space-y-1.5">
            <Label>Asunto</Label>
            <Input
              value={form.asunto}
              onChange={(e) => setForm((f) => ({ ...f, asunto: e.target.value }))}
              placeholder="Asunto del comunicado"
              data-testid="comunicado-asunto"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Mensaje</Label>
            <Textarea
              rows={8}
              value={form.mensaje}
              onChange={(e) => setForm((f) => ({ ...f, mensaje: e.target.value }))}
              placeholder="Texto del comunicado…"
              data-testid="comunicado-mensaje"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Centro</Label>
              <Select
                value={form.centro_id || "todos"}
                disabled={esManager}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, centro_id: v === "todos" ? "" : v, departamento_id: esManager ? f.departamento_id : "" }))
                }
              >
                <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {centros.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Departamento</Label>
              <Select
                value={form.departamento_id || "todos"}
                disabled={esManager}
                onValueChange={(v) => setForm((f) => ({ ...f, departamento_id: v === "todos" ? "" : v }))}
              >
                <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  {!esManager && <SelectItem value="todos">Todos</SelectItem>}
                  {deptsFiltrados.map((d) => (
                    <SelectItem key={d.id} value={String(d.id)}>{d.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {esManager && (
                <p className="text-[11px] text-zinc-400">Como encargado, el comunicado se limita a tu departamento.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Puesto</Label>
              <Select
                value={form.puesto_id || "todos"}
                onValueChange={(v) => setForm((f) => ({ ...f, puesto_id: v === "todos" ? "" : v }))}
              >
                <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {puestos.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Trabajador concreto</Label>
              <Select
                value={form.usuario_id || "todos"}
                onValueChange={(v) => setForm((f) => ({ ...f, usuario_id: v === "todos" ? "" : v }))}
              >
                <SelectTrigger><SelectValue placeholder="Todos los filtrados" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los filtrados</SelectItem>
                  {empleados.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.nombre} {u.apellidos}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2 rounded-xl border border-zinc-100 bg-zinc-50 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">Vía de comunicación</p>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.canal_email}
                onCheckedChange={(v) => setForm((f) => ({ ...f, canal_email: !!v }))}
              />
              Email (por defecto)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.canal_whatsapp}
                onCheckedChange={(v) => setForm((f) => ({ ...f, canal_whatsapp: !!v }))}
              />
              WhatsApp
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.canal_sms}
                onCheckedChange={(v) => setForm((f) => ({ ...f, canal_sms: !!v }))}
              />
              SMS
            </label>
            <p className="text-xs text-zinc-500">Además siempre se crea un aviso in-app en Mis avisos.</p>
          </div>

          <Button onClick={enviar} disabled={enviando} className="gap-2" data-testid="comunicado-enviar">
            <Send className="h-4 w-4" />
            {enviando ? "Enviando…" : "Enviar comunicado"}
          </Button>
        </div>

        <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-zinc-900">Destinatarios</p>
          <p className="mt-1 text-2xl font-light text-indigo-700">{preview?.total ?? "—"}</p>
          <p className="text-xs text-zinc-500">personas con los filtros actuales</p>
          <ul className="mt-4 max-h-96 space-y-2 overflow-y-auto text-sm">
            {(preview?.usuarios || []).map((u) => (
              <li key={u.id} className="rounded-lg border border-zinc-100 px-3 py-2">
                <p className="font-medium text-zinc-800">{u.nombre}</p>
                <p className="text-xs text-zinc-500">{u.email}{u.departamento ? ` · ${u.departamento}` : ""}</p>
              </li>
            ))}
            {(preview?.total || 0) > 50 && (
              <li className="text-xs text-zinc-400">… y {(preview.total - 50)} más</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
